import { z } from "zod";
// ── Enums / constants ──────────────────────────────────────────────
export const STUDIO_CATEGORIES = [
    "Makeup Artist",
    "Photographer",
    "Wedding Organizer",
    "Nail Artist",
    "Henna Artist",
    "Hair Stylist",
    "Lash Artist",
    "Dekorasi",
    "Event Vendor",
    "Other",
];
// Ticket #8: the five bookori statuses. `confirmed` was dropped; migration 0009 maps its rows to
// `paid`. D1 stores the status as text, so no column change.
export const BOOKING_STATUSES = ["pending", "dp_received", "paid", "completed", "cancelled"];
export const BOOKING_STATUS_LABELS = {
    pending: "Pending",
    dp_received: "DP received",
    paid: "Paid",
    completed: "Selesai (Done)",
    cancelled: "Cancelled",
};
/** Statuses the dashboard counts as money in: everything but `cancelled`. */
export const BOOKING_STATUSES_ACTIVE = ["pending", "dp_received", "paid", "completed"];
// ── Studio ─────────────────────────────────────────────────────────
export const StudioSchema = z.object({
    id: z.string(),
    accountId: z.string(),
    name: z.string(),
    slug: z.string(),
    category: z.enum(STUDIO_CATEGORIES),
    whatsapp: z.string().default(""),
    province: z.string().default(""),
    city: z.string().default(""),
    logoUrl: z.string().nullable().default(null),
    // Ticket #20: shown in the collaboration directory.
    bio: z.string().default(""),
    email: z.string().default(""),
    createdAt: z.number(),
    updatedAt: z.number(),
});
export const StudioProfileInput = z.object({
    name: z.string().min(2).max(120),
    category: z.enum(STUDIO_CATEGORIES),
    whatsapp: z.string().max(20).optional().default(""),
    province: z.string().max(80).optional().default(""),
    city: z.string().max(80).optional().default(""),
    // Ticket #20.
    bio: z.string().max(1000).optional().default(""),
    email: z.string().email().max(200).optional().or(z.literal("")).default(""),
});
/**
 * PATCH body for the studio profile: every field optional and no defaults, so a partial
 * edit (`Simpan lokasi` sends three fields) never resets the stored values. A `slug` here
 * is checked against the other studios first.
 */
export const StudioProfileUpdateInput = z.object({
    name: z.string().min(2).max(120).optional(),
    category: z.enum(STUDIO_CATEGORIES).optional(),
    whatsapp: z.string().max(20).optional(),
    province: z.string().max(80).optional(),
    city: z.string().max(80).optional(),
    bio: z.string().max(1000).optional(),
    email: z.union([z.string().email().max(200), z.literal("")]).optional(),
    slug: z.string().regex(/^[a-z0-9\-]+$/, "slug may only hold a-z, 0-9 and dashes").min(3).max(60).optional(),
});
// ── Service ────────────────────────────────────────────────────────
export const ServiceSchema = z.object({
    id: z.string(),
    studioId: z.string(),
    name: z.string().min(1).max(120),
    description: z.string().max(500).default(""),
    price: z.number().int().min(0),
    durationMin: z.number().int().min(1).optional().nullable().default(null),
    active: z.boolean().default(true),
    publicVisible: z.boolean().default(true),
    autoChecked: z.boolean().default(false),
    category: z.string().max(60).default(""),
    sortOrder: z.number().int().default(0),
});
export const ServiceInput = ServiceSchema.omit({ id: true, studioId: true });
/** PATCH body: every field optional and no defaults, so a partial body never resets stored values. */
export const ServiceUpdateInput = z.object({
    name: z.string().min(1).max(120).optional(),
    description: z.string().max(500).optional(),
    price: z.number().int().min(0).optional(),
    durationMin: z.number().int().min(1).nullable().optional(),
    active: z.boolean().optional(),
    publicVisible: z.boolean().optional(),
    autoChecked: z.boolean().optional(),
    category: z.string().max(60).optional(),
    sortOrder: z.number().int().optional(),
});
// ── Booking ────────────────────────────────────────────────────────
export const DateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "date must be YYYY-MM-DD");
export const TimeSchema = z.string().regex(/^\d{2}:\d{2}$/, "time must be HH:mm");
// ── Public cart (ticket #6) ───────────────────────────────────────
// One line per service on the public page, `{ serviceId, quantity }`. The API rebuilds
// name and price from the services table, so a client-supplied price can never win.
export const CartLineInput = z.object({
    serviceId: z.string().min(1),
    quantity: z.number().int().min(1).max(99).default(1),
});
/** A stored cart line: `bookings.items` holds these. */
export const CartItemSchema = z.object({
    serviceId: z.string(),
    name: z.string(),
    price: z.number().int().min(0),
    quantity: z.number().int().min(1),
});
/** Services sum. Transport and discount stay in their own columns. */
export function cartSubtotal(items) {
    return items.reduce((sum, item) => sum + item.price * item.quantity, 0);
}
/** What the page shows as `Total`: the services sum plus the transport fee. */
export function cartTotal(items, transportFee) {
    return cartSubtotal(items) + transportFee;
}
export const BookingSchema = z.object({
    id: z.string(),
    studioId: z.string(),
    serviceId: z.string(),
    serviceName: z.string(),
    clientName: z.string().min(1).max(120),
    clientPhone: z.string().max(20).default(""),
    clientEmail: z.string().email().max(200).optional().nullable().default(null),
    note: z.string().max(1000).default(""),
    date: DateSchema,
    time: TimeSchema,
    status: z.enum(BOOKING_STATUSES),
    totalPrice: z.number().int().min(0),
    dpAmount: z.number().int().min(0).default(0),
    dpPaidAt: z.number().nullable().default(null),
    proofUrl: z.string().nullable().default(null),
    // Pro booking fields.
    formId: z.string().nullable().default(null),
    peopleCount: z.number().int().min(1).default(1),
    instagram: z.string().default(""),
    address: z.string().default(""),
    transportFee: z.number().int().min(0).default(0),
    discount: z.number().int().min(0).default(0),
    rescheduleCount: z.number().int().min(0).default(0),
    extraItems: z.array(z.object({ label: z.string(), price: z.number() })).default([]),
    customFields: z.record(z.string(), z.string()).default({}),
    teamMemberIds: z.array(z.string()).default([]),
    // Ticket #6.
    items: z.array(CartItemSchema).default([]),
    quantity: z.number().int().min(1).default(1),
    addressLat: z.number().nullable().default(null),
    addressLng: z.number().nullable().default(null),
    contractAccepted: z.boolean().default(false),
    source: z.string().default("manual"),
    // Ticket #8: order notes, vendors and the status timestamps.
    discountNote: z.string().max(200).default(""),
    internalNote: z.string().max(1000).default(""),
    extraNote: z.string().max(1000).default(""),
    vendorIds: z.array(z.string()).default([]),
    dpReceivedAt: z.number().nullable().default(null),
    paidAt: z.number().nullable().default(null),
    completedAt: z.number().nullable().default(null),
    archivedAt: z.number().nullable().default(null),
    // Ticket #16 tail.
    externalEventId: z.string().nullable().default(null),
    externalUpdatedAt: z.number().nullable().default(null),
    createdAt: z.number(),
    updatedAt: z.number(),
});
export const PublicBookingInput = z.object({
    serviceId: z.string(),
    // Built-in answers are optional here: `fieldsConfig` on the form decides what is
    // `Wajib`. A form that hides a field sends nothing, so "" is a legal value.
    clientName: z.string().max(120).optional().default(""),
    clientPhone: z.string().max(20).optional().default(""),
    clientEmail: z.string().email().max(200).optional().nullable().default(null),
    note: z.string().max(1000).optional().default(""),
    date: z.union([DateSchema, z.literal("")]).optional().default(""),
    time: z.union([TimeSchema, z.literal("")]).optional().default(""),
    dpAmount: z.number().int().min(0).max(50_000_000).default(0),
    // Pro fields (numbers arrive as strings via multipart, coerced in the route).
    peopleCount: z.number().int().min(1).max(100).optional().default(1),
    instagram: z.string().max(120).optional().default(""),
    address: z.string().max(500).optional().default(""),
    formId: z.string().optional().nullable().default(null),
    contractAccepted: z.boolean().optional().default(false),
    // Ticket #6: the public page's cart. `items` wins when it holds a line; `serviceId`
    // stays the one-service fallback (a page cached from before the deploy still posts it).
    items: z.array(CartLineInput).optional().default([]),
    addressLat: z.number().nullable().optional().default(null),
    addressLng: z.number().nullable().optional().default(null),
});
export const AdminBookingCreate = PublicBookingInput.extend({
    status: z.enum(BOOKING_STATUSES).optional().default("pending"),
    // Pro fields.
    formId: z.string().optional().nullable().default(null),
    peopleCount: z.number().int().min(1).max(100).optional().default(1),
    instagram: z.string().max(120).optional().default(""),
    address: z.string().max(500).optional().default(""),
    transportFee: z.number().int().min(0).max(50_000_000).optional().default(0),
    discount: z.number().int().min(0).max(50_000_000).optional().default(0),
    extraItems: z.array(z.object({ label: z.string().min(1).max(120), price: z.number().int().min(0) })).optional().default([]),
    customFields: z.record(z.string(), z.string()).optional().default({}),
    teamMemberIds: z.array(z.string()).optional().default([]),
    // Ticket #8: the manual modal's order notes and vendors.
    discountNote: z.string().max(200).optional().default(""),
    internalNote: z.string().max(1000).optional().default(""),
    extraNote: z.string().max(1000).optional().default(""),
    vendorIds: z.array(z.string()).optional().default([]),
});
export const BookingUpdateInput = z.object({
    status: z.enum(BOOKING_STATUSES).optional(),
    date: DateSchema.optional(),
    time: TimeSchema.optional(),
    clientName: z.string().min(1).max(120).optional(),
    clientPhone: z.string().min(7).max(20).optional(),
    clientEmail: z.string().email().max(200).optional().nullable(),
    note: z.string().max(1000).optional(),
    instagram: z.string().max(120).optional(),
    address: z.string().max(500).optional(),
    peopleCount: z.number().int().min(1).max(100).optional(),
    transportFee: z.number().int().min(0).max(50_000_000).optional(),
    discount: z.number().int().min(0).max(50_000_000).optional(),
    dpAmount: z.number().int().min(0).max(50_000_000).optional(),
    extraItems: z.array(z.object({ label: z.string().min(1).max(120), price: z.number().int().min(0) })).optional(),
    customFields: z.record(z.string(), z.string()).optional(),
    teamMemberIds: z.array(z.string()).optional(),
});
export const BookingStatusInput = z.object({
    status: z.enum(BOOKING_STATUSES),
});
// ── Booking forms ──────────────────────────────────────────────────
// Built-in booking fields in their default order. `key` is the payload field on the
// public booking POST; the owner renames labels and reorders through `fieldsConfig`.
export const FORM_BUILTIN_FIELDS = [
    { key: "clientName", label: "Nama lengkap" },
    { key: "clientPhone", label: "WhatsApp" },
    { key: "instagram", label: "Instagram" },
    { key: "peopleCount", label: "Jumlah orang" },
    { key: "date", label: "Tanggal booking" },
    { key: "time", label: "Jam booking" },
    { key: "address", label: "Lokasi/venue" },
    { key: "note", label: "Catatan" },
    { key: "clientEmail", label: "Email" },
];
// A booking needs a client, a contact and a schedule: those stay `Wajib` unless the
// owner changes them, so an existing studio never silently loses an answer.
export const FORM_DEFAULT_REQUIRED_KEYS = ["clientName", "clientPhone", "date", "time"];
export const FormFieldConfigSchema = z.object({
    key: z.string(),
    label: z.string(),
    required: z.boolean().default(false),
    visible: z.boolean().default(true),
});
export const CustomFieldSchema = z.object({
    id: z.string(),
    label: z.string().min(1).max(120),
    type: z.enum(["text", "textarea", "select"]).default("text"),
    required: z.boolean().default(false),
    options: z.array(z.string()).default([]),
});
export const BookingFormSchema = z.object({
    id: z.string(),
    studioId: z.string(),
    name: z.string(),
    slug: z.string(),
    description: z.string(),
    active: z.boolean(),
    dpMinPercent: z.number().int().min(0).max(100),
    requireProof: z.boolean(),
    requireContract: z.boolean(),
    aiValidation: z.boolean(),
    themeColor: z.string(),
    bannerKey: z.string().nullable(),
    bannerUrl: z.string().nullable(),
    category: z.string().nullable(),
    fieldsConfig: z.array(FormFieldConfigSchema),
    customFields: z.array(CustomFieldSchema),
    serviceIds: z.array(z.string()),
    createdAt: z.number(),
});
/** The built-in field set a form starts with: all visible, the essentials `Wajib`. */
export function defaultFieldsConfig() {
    const required = new Set(FORM_DEFAULT_REQUIRED_KEYS);
    return FORM_BUILTIN_FIELDS.map((field) => ({
        key: field.key,
        label: field.label,
        required: required.has(field.key),
        visible: true,
    }));
}
export const BookingFormInput = z.object({
    name: z.string().min(2).max(120),
    slug: z.string().min(1).max(60).regex(/^[a-z0-9-]+$/, "Hanya huruf kecil, angka dan tanda hubung"),
    description: z.string().max(500).optional().default(""),
    active: z.boolean().optional().default(true),
    dpMinPercent: z.number().int().min(0).max(100).optional().default(0),
    requireProof: z.boolean().optional().default(false),
    requireContract: z.boolean().optional().default(false),
    aiValidation: z.boolean().optional().default(false),
    themeColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional().default("#e26d5a"),
    category: z.string().max(60).nullable().optional().default(null),
    bannerKey: z.string().nullable().optional().default(null),
    fieldsConfig: z.array(FormFieldConfigSchema).optional().default([]),
    customFields: z.array(CustomFieldSchema).optional().default([]),
    serviceIds: z.array(z.string()).optional().default([]),
});
/** PATCH body: every field optional and no defaults, so a partial body never resets stored values. */
export const BookingFormUpdateInput = z.object({
    name: z.string().min(2).max(120).optional(),
    slug: z.string().min(1).max(60).regex(/^[a-z0-9-]+$/, "Hanya huruf kecil, angka dan tanda hubung").optional(),
    description: z.string().max(500).optional(),
    active: z.boolean().optional(),
    dpMinPercent: z.number().int().min(0).max(100).optional(),
    requireProof: z.boolean().optional(),
    requireContract: z.boolean().optional(),
    aiValidation: z.boolean().optional(),
    themeColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    category: z.string().max(60).nullable().optional(),
    bannerKey: z.string().nullable().optional(),
    fieldsConfig: z.array(FormFieldConfigSchema).optional(),
    customFields: z.array(CustomFieldSchema).optional(),
    serviceIds: z.array(z.string()).optional(),
});
// ── Team members ───────────────────────────────────────────────────
export const FEE_TYPES = ["percent", "fixed"];
export const FEE_TYPE_LABELS = { percent: "Persentase (%)", fixed: "Nominal tetap (Rp)" };
export const COMMISSION_STATUSES = ["pending", "paid"];
export const TeamMemberSchema = z.object({
    id: z.string(),
    studioId: z.string(),
    name: z.string(),
    role: z.string(),
    whatsapp: z.string(),
    feePercent: z.number().int().min(0).max(100),
    feeType: z.enum(FEE_TYPES),
    feeAmount: z.number().int().min(0),
    commissionEnabled: z.boolean(),
    active: z.boolean(),
    createdAt: z.number(),
});
export const TeamMemberInput = z.object({
    name: z.string().min(1).max(120),
    role: z.string().max(60).optional().default("Staff"),
    whatsapp: z.string().max(20).optional().default(""),
    feePercent: z.number().int().min(0).max(100).optional().default(0),
    feeType: z.enum(FEE_TYPES).optional().default("percent"),
    feeAmount: z.number().int().min(0).optional().default(0),
    commissionEnabled: z.boolean().optional().default(true),
    active: z.boolean().optional().default(true),
});
/** PATCH body: no defaults, so a partial edit never resets the stored fee fields. */
export const TeamMemberUpdateInput = z.object({
    name: z.string().min(1).max(120).optional(),
    role: z.string().max(60).optional(),
    whatsapp: z.string().max(20).optional(),
    feePercent: z.number().int().min(0).max(100).optional(),
    feeType: z.enum(FEE_TYPES).optional(),
    feeAmount: z.number().int().min(0).optional(),
    commissionEnabled: z.boolean().optional(),
    active: z.boolean().optional(),
});
/** One member's money row on one booking. `clientName`/`date` come from the booking join. */
export const TeamAssignmentSchema = z.object({
    id: z.string(),
    studioId: z.string(),
    bookingId: z.string(),
    teamMemberId: z.string(),
    feeType: z.enum(FEE_TYPES),
    feeAmount: z.number().int().min(0),
    feeTotal: z.number().int().min(0),
    commissionStatus: z.enum(COMMISSION_STATUSES),
    createdAt: z.number(),
    clientName: z.string(),
    date: z.string(),
});
const CommissionTotals = { pending: z.number().int().min(0), paid: z.number().int().min(0), total: z.number().int().min(0) };
/** A member's commissions inside the asked range; members with no rows report zeros. */
export const CommissionMemberSchema = z.object({
    memberId: z.string(),
    name: z.string(),
    ...CommissionTotals,
    count: z.number().int().min(0),
    assignments: z.array(TeamAssignmentSchema),
});
export const CommissionsSchema = z.object({
    from: z.string(),
    to: z.string(),
    members: z.array(CommissionMemberSchema),
});
export const TeamMemberFeesSchema = z.object({
    member: TeamMemberSchema,
    ...CommissionTotals,
    count: z.number().int().min(0),
    assignments: z.array(TeamAssignmentSchema),
});
/** `POST /api/bookings/{id}/assignments`: the handler list to materialise. */
export const BookingAssignmentsInput = z.object({
    teamMemberIds: z.array(z.string()).max(20),
});
export const CommissionStatusInput = z.object({
    status: z.enum(COMMISSION_STATUSES),
});
// ── Notifications ──────────────────────────────────────────────────
export const NotificationSchema = z.object({
    id: z.string(),
    studioId: z.string(),
    type: z.string(),
    title: z.string(),
    body: z.string(),
    link: z.string(),
    read: z.boolean(),
    createdAt: z.number(),
});
/** The inbox page: the newest rows plus the counters the header shows (ticket #19). */
export const NotificationsPayloadSchema = z.object({
    items: z.array(NotificationSchema),
    unread: z.number(),
    total: z.number(),
});
/** Which events push to the owner's browser; all default on except the digest. */
export const NotificationPreferencesSchema = z.object({
    bookingCreated: z.boolean(),
    dpReceived: z.boolean(),
    reschedule: z.boolean(),
    dailyDigest: z.boolean(),
    reminderHours: z.number(),
});
export const NOTIFICATION_DEFAULT_PREFERENCES = {
    bookingCreated: true,
    dpReceived: true,
    reschedule: true,
    dailyDigest: false,
    reminderHours: 24,
};
/** A partial edit must not write the defaults back (the `.partial()` zod trap). */
export const NotificationPreferencesUpdateInput = NotificationPreferencesSchema.partial();
/** A browser push subscription as `PushManager.subscribe()` hands it over. */
export const PushSubscriptionInputSchema = z.object({
    endpoint: z.string().min(1),
    keys: z.object({ p256dh: z.string().min(1), auth: z.string().min(1) }),
});
// ── Studio settings (booking + invoice) ────────────────────────────
export const DP_ACCOUNT_SCHEMA = z.object({
    id: z.string(),
    bank: z.string().min(1).max(40),
    number: z.string().min(3).max(40),
    name: z.string().min(1).max(80),
});
/** `POST /api/settings/dp-accounts` body: the id is the server's to assign. */
export const DP_ACCOUNT_INPUT = DP_ACCOUNT_SCHEMA.omit({ id: true });
export const BookingSettingsSchema = z.object({
    // transportMode: none = free / studio pick-up, fixed = flat fee, distance = per km.
    transportMode: z.enum(["none", "fixed", "distance"]).default("none"),
    transportFee: z.number().int().min(0).max(50_000_000).default(0),
    transportPerKm: z.number().int().min(0).max(50_000).default(0),
    // The reschedule ceiling is gone (owner decision 2026-09-18): the studio moves a booking as
    // often as it likes, and `bookings.rescheduleCount` keeps counting for information only.
    dpAccounts: z.array(DP_ACCOUNT_SCHEMA).default([]),
    paymentMethods: z.array(z.string()).default(["transfer"]),
    qrisKey: z.string().nullable().default(null),
});
export const InvoiceSettingsSchema = z.object({
    businessName: z.string().max(120).default(""),
    showLogo: z.boolean().default(true),
    showWhatsapp: z.boolean().default(true),
    showEmail: z.boolean().default(true),
    // Due date = event date − 3 days; off means the invoice prints no due date.
    showDueDate: z.boolean().default(true),
    themeColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).default("#e26d5a"),
    numberFormat: z.string().max(80).default("INV-{year}-{seq}"),
    // The invoice's money labels; the reference product ships these English strings.
    labelDp: z.string().max(40).default("Down payment"),
    labelRemaining: z.string().max(40).default("Remaining"),
    // Free text block printed on the invoice (bank account, e-wallet, …).
    paymentInfo: z.string().max(500).default(""),
    // One of INVOICE_DATE_FORMATS; anything else falls back to `long`.
    dateFormat: z.string().max(40).default("long"),
    footer: z.string().max(300).default("Terima kasih telah mempercayakan momen spesialmu kepada kami. ✨"),
    waTemplate: z.string().max(2000).default("Halo {customer_name}! Ini detail booking kamu:\n{services}\nTanggal: {date} {time}\nTotal: {total}\nDP: {dp}\nSisa: {remaining}\n\n{invoice_url}\n\nBalas pesan ini jika ada pertanyaan ya."),
});
/** Invoice date styles — the `dateFormat` setting picks one. */
export const INVOICE_DATE_FORMATS = ["full", "long", "short"];
/** Sample rendering per format, used as the option labels in the settings UI. */
export const INVOICE_DATE_LABELS = {
    full: "Rabu, 10 Juni 2026",
    long: "10 Juni 2026",
    short: "10/06/2026",
};
const INVOICE_DATE_STYLE = {
    full: { weekday: "long", day: "numeric", month: "long", year: "numeric" },
    long: { day: "numeric", month: "long", year: "numeric" },
    // Zero-padded, the way `INVOICE_DATE_LABELS.short` promises it.
    short: { day: "2-digit", month: "2-digit", year: "numeric" },
};
/** Render a `YYYY-MM-DD` date in the studio's invoice date style. */
export function formatDateId(date, dateFormat) {
    const style = INVOICE_DATE_FORMATS.includes(dateFormat ?? "")
        ? dateFormat
        : "long";
    return new Date(`${date}T00:00:00`).toLocaleDateString("id-ID", INVOICE_DATE_STYLE[style]);
}
/**
 * Calendar settings (ticket #16). `Full Booked` is a manual marker the owner sets (decision 7):
 * no threshold, no capacity math — just the dates they closed by hand.
 */
export const CalendarSettingsSchema = z.object({
    fullBookedDates: z.array(z.string()),
});
export const BrandingSettingsSchema = z.object({
    /** Where the studio logo shows: the public booking page, the invoice, the studio profile. */
    logoOnPublicForm: z.boolean().default(true),
    logoOnInvoice: z.boolean().default(true),
    logoOnProfile: z.boolean().default(true),
});
/** PATCH body: every field optional and no defaults, so a partial edit changes only what it names. */
export const BrandingSettingsUpdateInput = z.object({
    logoOnPublicForm: z.boolean().optional(),
    logoOnInvoice: z.boolean().optional(),
    logoOnProfile: z.boolean().optional(),
});
/**
 * Which components the reports page subtracts from revenue (ticket #12). A disabled component
 * stays in the payload for display — it only leaves `net`. `transport` ships off: most bookings
 * carry no transport fee, and an accidental deduction reads as a wrong margin.
 */
export const ReportComponentsSchema = z.object({
    team: z.boolean().default(true),
    vendor: z.boolean().default(true),
    transport: z.boolean().default(false),
    expenses: z.boolean().default(true),
});
/** `PUT /api/reports/settings` body: names only the toggles it changes. */
export const ReportComponentsUpdateInput = z.object({
    team: z.boolean().optional(),
    vendor: z.boolean().optional(),
    transport: z.boolean().optional(),
    expenses: z.boolean().optional(),
});
export const StudioSettingsSchema = z.object({
    booking: BookingSettingsSchema,
    invoice: InvoiceSettingsSchema,
    calendar: CalendarSettingsSchema,
    branding: BrandingSettingsSchema,
    reports: ReportComponentsSchema,
});
/**
 * `PUT /api/settings` body. Written without `.default()` on purpose: a default still
 * applies under `.partial()`, so a partial edit would write every omitted key back to
 * its default — the trap #7 fixed for services and #14 for expenses.
 */
export const BookingSettingsUpdateInput = z.object({
    transportMode: z.enum(["none", "fixed", "distance"]).optional(),
    transportFee: z.number().int().min(0).max(50_000_000).optional(),
    transportPerKm: z.number().int().min(0).max(50_000).optional(),
    dpAccounts: z.array(DP_ACCOUNT_SCHEMA).optional(),
    paymentMethods: z.array(z.string()).optional(),
    qrisKey: z.string().nullable().optional(),
});
export const InvoiceSettingsUpdateInput = z.object({
    businessName: z.string().max(120).optional(),
    showLogo: z.boolean().optional(),
    showWhatsapp: z.boolean().optional(),
    showEmail: z.boolean().optional(),
    showDueDate: z.boolean().optional(),
    themeColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    numberFormat: z.string().max(80).optional(),
    labelDp: z.string().max(40).optional(),
    labelRemaining: z.string().max(40).optional(),
    paymentInfo: z.string().max(500).optional(),
    dateFormat: z.string().max(40).optional(),
    footer: z.string().max(300).optional(),
    waTemplate: z.string().max(2000).optional(),
});
/**
 * `PATCH /api/settings` body (ticket #20): a partial update that names only what it changes.
 * `calendar` is never in this body — the full-booked markers are #16's and a settings save
 * must not drop them.
 */
export const SettingsUpdateInput = z.object({
    branding: BrandingSettingsUpdateInput.optional(),
    reports: ReportComponentsUpdateInput.optional(),
});
export const DEFAULT_STUDIO_SETTINGS = {
    booking: {
        transportMode: "none",
        transportFee: 0,
        transportPerKm: 0,
        dpAccounts: [],
        paymentMethods: ["transfer"],
        qrisKey: null,
    },
    calendar: {
        fullBookedDates: [],
    },
    branding: {
        logoOnPublicForm: true,
        logoOnInvoice: true,
        logoOnProfile: true,
    },
    reports: {
        team: true,
        vendor: true,
        transport: false,
        expenses: true,
    },
    invoice: {
        businessName: "",
        showLogo: true,
        showWhatsapp: true,
        showEmail: true,
        showDueDate: true,
        themeColor: "#e26d5a",
        numberFormat: "INV-{year}-{seq}",
        labelDp: "Down payment",
        labelRemaining: "Remaining",
        paymentInfo: "",
        dateFormat: "long",
        footer: "Terima kasih telah mempercayakan momen spesialmu kepada kami. ✨",
        waTemplate: "Halo {customer_name}! Ini detail booking kamu:\n{services}\nTanggal: {date} {time}\nTotal: {total}\nDP: {dp}\nSisa: {remaining}\n\n{invoice_url}\n\nBalas pesan ini jika ada pertanyaan ya.",
    },
};
// ── Calendar (ticket #16) ──────────────────────────────────────────
/** `PATCH /api/calendar/full-booked` — the manual marker toggle (decision 7). */
export const FullBookedInput = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Tanggal tidak valid"),
    booked: z.boolean(),
});
/** `GET /api/calendar` — the rows for the window, the per-day counts and the marker dates. */
export const CalendarPayloadSchema = z.object({
    from: z.string(),
    to: z.string(),
    bookings: z.array(BookingSchema),
    counts: z.record(z.string(), z.number()),
    fullBookedDates: z.array(z.string()),
});
// ── Reports ────────────────────────────────────────────────────────
export const ReportSchema = z.object({
    from: z.string(),
    to: z.string(),
    bookingsCount: z.number(),
    statusCounts: z.record(z.enum(BOOKING_STATUSES), z.number()),
    revenue: z.number(),
    expenses: z.number(),
    transportCost: z.number(),
    teamCost: z.number(),
    vendorCost: z.number(),
    gross: z.number(),
    net: z.number(),
    margin: z.number(),
    dpReceived: z.number(),
    dpPending: z.number(),
    remaining: z.number(),
    topServices: z.array(z.object({ name: z.string(), count: z.number(), revenue: z.number() })),
    /** The toggles `net` was computed with, so the page can mark a disabled card `(off)`. */
    components: ReportComponentsSchema,
});
/** The graph's metric; `profit` follows the same component toggles as `net`. */
export const REPORT_METRICS = ["revenue", "bookings", "profit"];
/**
 * `GET /api/reports/series` — one point per day (ranges under 60 days) or per month, zero
 * filled. `date` is `YYYY-MM-DD` for a day bucket and `YYYY-MM` for a month one; the point
 * sum equals the matching KPI for the same range.
 */
export const ReportSeriesSchema = z.object({
    from: z.string(),
    to: z.string(),
    metric: z.enum(REPORT_METRICS),
    granularity: z.enum(["day", "month"]),
    points: z.array(z.object({ date: z.string(), value: z.number() })),
});
// ── Expense ────────────────────────────────────────────────────────
export const ExpenseSchema = z.object({
    id: z.string(),
    studioId: z.string(),
    title: z.string().min(1).max(120),
    amount: z.number().int().min(0),
    date: DateSchema,
    /** Empty string means uncategorised; the UI renders `Tanpa kategori`. */
    category: z.string().max(60),
    paymentMethod: z.string().max(60),
    note: z.string().max(500),
    createdAt: z.number(),
});
export const ExpenseInput = z.object({
    title: z.string().min(1).max(120),
    amount: z.number().int().min(0),
    date: DateSchema,
    category: z.string().max(60).default(""),
    paymentMethod: z.string().max(60).default(""),
    note: z.string().max(500).default(""),
});
/** PATCH body: no `.default()` here — a default still applies under `.partial()`. */
export const ExpenseUpdateInput = z.object({
    title: z.string().min(1).max(120).optional(),
    amount: z.number().int().min(0).optional(),
    date: DateSchema.optional(),
    category: z.string().max(60).optional(),
    paymentMethod: z.string().max(60).optional(),
    note: z.string().max(500).optional(),
});
/** A stored expense category or payment method (same shape, different tables). */
export const ExpenseCategorySchema = z.object({
    id: z.string(),
    studioId: z.string(),
    name: z.string().min(1).max(60),
    createdAt: z.number(),
});
export const PaymentMethodSchema = z.object({
    id: z.string(),
    studioId: z.string(),
    name: z.string().min(1).max(60),
    createdAt: z.number(),
});
/** Body for `POST /api/expenses/categories` and `POST /api/expenses/payment-methods`. */
export const NamedInput = z.object({ name: z.string().trim().min(1).max(60) });
// ── Dashboard ──────────────────────────────────────────────────────
export const TopServiceSchema = z.object({
    name: z.string(),
    count: z.number(),
    revenue: z.number(),
});
export const DashboardSeriesPointSchema = z.object({
    date: DateSchema,
    revenue: z.number(),
    bookings: z.number(),
});
export const DashboardSchema = z.object({
    monthRevenue: z.number(),
    monthOmzet: z.number(),
    monthDpCollected: z.number(),
    monthDpPending: z.number(),
    monthExpenses: z.number(),
    monthBookings: z.number(),
    tip: z.string().min(1),
    series: z.array(DashboardSeriesPointSchema),
    clientsCount: z.number(),
    statusCounts: z.record(z.enum(BOOKING_STATUSES), z.number()),
    topServices: z.array(TopServiceSchema),
    recentBookings: z.array(BookingSchema),
});
// ── Invoice (printable receipt) ────────────────────────────────────
export const InvoiceSchema = z.object({
    number: z.string(),
    issuedAt: z.number(),
    studio: StudioSchema.pick({ name: true, whatsapp: true, city: true, logoUrl: true }),
    service: ServiceSchema.pick({ name: true, price: true }),
    booking: BookingSchema,
    lineItems: z.array(z.object({ label: z.string(), price: z.number() })),
    subtotal: z.number(),
    transportFee: z.number(),
    discount: z.number(),
    dp: z.number(),
    remaining: z.number(),
    /** Event date − 3 days, or null when the studio turns the due date off. */
    dueDate: z.string().nullable(),
    settings: InvoiceSettingsSchema,
    paymentInfo: z.string(),
    waMessage: z.string(),
});
/** `POST /api/invoice-settings/preview` — the sample number and message. */
export const InvoicePreviewSchema = z.object({
    number: z.string(),
    message: z.string(),
});
// ── Auth ───────────────────────────────────────────────────────────
export const AccountSchema = z.object({
    id: z.string(),
    email: z.string().email(),
    name: z.string(),
    picture: z.string().nullable().default(null),
    createdAt: z.number(),
});
export const AuthResponseSchema = z.object({
    accessToken: z.string(),
    account: AccountSchema,
    studio: StudioSchema.nullable(),
});
// Assembles the default studio name for a brand-new account.
export function defaultStudioSlug(name) {
    const base = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    const slug = base || "studio";
    return `${slug.slice(0, 40)}-${Math.random().toString(36).slice(2, 6)}`;
}
export function formatRp(amount) {
    const n = new Intl.NumberFormat("id-ID").format(amount);
    return `Rp ${n}`;
}
